/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Button, Headline, Paragraph, TopModal } from '@cambridgeassessment/cambridge-ui';
import NavigationPrompt from 'react-router-navigation-prompt';
import { deleteAllComponents } from '../../../../store/components/actions';
import Loader from '../../../../components/Loader/Loader';
import thunks from '../../../../store/components/thunks';
import ComponentFC from '../Details/ComponentFC';
import { RootState } from '../../../../store';
import { StyledContainer } from '../../Content.styles';
import { getActiveSeriesData } from '../../../../store/series/thunks';
import { ButtonText, StyledModalChildrenContainer, StyledCentralModal } from '../Details/styles';
import { Roles } from '@cambridgeassessment/lib-ee-commons';
import { ENTRIES_RECEIVED_ACTIVE_SERIES_TEXT } from '../../../../constants';

interface RouteProps {
  componentId: string | undefined;
}

interface PromptMethods {
  onCancel: () => void;
  onConfirm: () => void;
}

export const ConfigureComponent = (): JSX.Element => {
  const dispatch = useDispatch();

  const params = useParams<RouteProps>();

  const {
    activeSeriesIds,
    seriesData,
    isLoading: isSeriesDataLoading,
  } = useSelector((state: RootState) => state.series);
  const { isLoading, component, hasError } = useSelector((state: RootState) => state.components);
  const { businessUnit, selectedRole } = useSelector((state: RootState) => state.userData);

  const [isSeriesModalOpen, setSeriesModalOpen] = useState<boolean>(false);
  const [isCompConfigBannerOpen, setCompConfigBannerOpen] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const isUserSuperAdmin = selectedRole?.name === Roles.SUBMIT_SUPER_ADMIN;

  useEffect(() => {
    dispatch(getActiveSeriesData());
    if (businessUnit?.id && activeSeriesIds?.length) {
      dispatch(thunks.getComponentWithEntriesCheck(businessUnit.id, activeSeriesIds, params?.componentId ?? ''));
    } else {
      dispatch(thunks.getComponent(params?.componentId ?? ''));
    }
    return () => {
      dispatch(deleteAllComponents());
    };
  }, [businessUnit?.id, activeSeriesIds?.length, dispatch]);

  useEffect(() => {
    if (component && seriesData && activeSeriesIds?.length) {
      const componentHasActiveSeries = component.series.some((series) => activeSeriesIds.includes(series.id));
      const isComponentConfigDisabled = componentHasActiveSeries || component.toBeConfirmed;

      if (!isUserSuperAdmin && isComponentConfigDisabled) {
        setIsDisabled(isComponentConfigDisabled);
      }

      setSeriesModalOpen(isComponentConfigDisabled && !!component?.hasEntries);
    }
  }, [seriesData, activeSeriesIds?.length, component?.hasEntries, dispatch]);

  useEffect(() => {
    if (component) {
      setCompConfigBannerOpen(component.toBeConfirmed);
    }
  }, [component]);

  if (isLoading || isSeriesDataLoading) {
    return <Loader />;
  }

  if (hasError || component == null) {
    return <div>No component found</div>;
  }

  const confirmationModalText = 'You must confirm copied component configurations before they can be edited and used.';
  const closeModal = (): void => setSeriesModalOpen(false);

  const componentText = (): JSX.Element => {
    if (component.toBeConfirmed) {
      return (
        <>
          <Headline>Review and confirm component configuration</Headline>
          <Paragraph>
            Review and confirm{' '}
            <b>
              {component.name} {component.number} (not confirmed)
            </b>{' '}
            rules, component and learner sections in order to use it. You can adjust the settings once they&apos;re
            confirmed.
          </Paragraph>
        </>
      );
    }
    return (
      <>
        <Headline>Configure component</Headline>
        <Paragraph>
          Configure component <b>{`Component ${component.number} - ${component.name}`}</b> rules, component and learner
          sections
        </Paragraph>
      </>
    );
  };

  return (
    <StyledContainer data-qa-id={`configure-component-page-${component.number}`}>
      {component.hasEntries && (
        <TopModal topPosition={64} dataQaId='entries-received-active-series' open={isSeriesModalOpen} fixed={false}>
          <StyledModalChildrenContainer>
            <div data-qa-id='entries-received-active-series-text'>{ENTRIES_RECEIVED_ACTIVE_SERIES_TEXT}</div>
            <Button className='closeModal' onClick={closeModal} qaId='close-modal' variant='text'>
              <ButtonText>Close</ButtonText>
            </Button>
          </StyledModalChildrenContainer>
        </TopModal>
      )}
      <TopModal topPosition={64} dataQaId='component-review-modal' open={isCompConfigBannerOpen} fixed={false}>
        <StyledModalChildrenContainer>
          <div>
            You must review and confirm copied component configurations before they can be edited and used. They have
            been copied from the previous syllabus version to this syllabus version, for all components where numbers
            match.
          </div>
        </StyledModalChildrenContainer>
      </TopModal>
      {componentText()}
      <ComponentFC key={`${component.name}-${0}`} component={component} index={0} disabled={isDisabled} fullTitle />
      <NavigationPrompt when={component.toBeConfirmed}>
        {({ onConfirm, onCancel }: PromptMethods) => (
          <StyledCentralModal
            dataQaId='Confirm-component'
            title='Component is not confirmed'
            body={confirmationModalText}
            open={component.toBeConfirmed}
            btnRightText='Review configuration'
            btnLeftText='Leave'
            onClickBtnRight={onCancel}
            onClickBtnLeft={onConfirm}
          />
        )}
      </NavigationPrompt>
    </StyledContainer>
  );
};
